import { useAdminStore } from '@admin/index'
import { Box, Container } from '@mui/material'
import { useTranslate } from '@tolgee/react'
import { Loader, useMinimalsTheme } from '@tom-ui/ui'
import { appStore, PATHS, useAsyncFetch, useFormStore, withAuthenticationRequired } from '@tom-ui/utils'
import { computed } from 'mobx'
import { observer } from 'mobx-react-lite'
import { useNavigate, useParams } from 'react-router'
import { RoleFormHeader } from './components/molecules/RoleFormHeader'
import { RoleForm } from './components/organisms/RoleForm'
import { useRoleMessageHandler } from './hooks/useRolesMessageHandler'
import { RoleFormData } from './models/role.model'

export const RoleFormPage = withAuthenticationRequired(
  observer(() => {
    const theme = useMinimalsTheme()
    const { t } = useTranslate()
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()
    const { roleStore } = useAdminStore()
    const { getExceptionMessage, getSuccessMessage } = useRoleMessageHandler()
    const formStore = useFormStore<RoleFormData>()

    const roleId = id ? Number(id) : null
    const isEdit = !!roleId && id !== 'new'

    const { loading } = useAsyncFetch(async () => {
      await Promise.all([roleStore.load(), roleStore.loadPermissions()])
    }, [roleStore])

    const selectedRole = computed(() =>
      isEdit ? roleStore.data.find(x => x.id === roleId) : undefined,
    ).get()

    const onSubmit = async () => {
      try {
        if (formStore.validate()) {
          if (isEdit) {
            await roleStore.updateRole({
              id: roleId,
              name: formStore.data.name,
              description: formStore.data.description,
              permissions: formStore.data.permissions,
            })
          } else {
            await roleStore.createRole({
              name: formStore.data.name,
              description: formStore.data.description,
              permissions: formStore.data.permissions,
            })
          }

          appStore.setShowAlert('success', getSuccessMessage(isEdit))
          navigate(PATHS.config.roles.url)
        }
      } catch (error: any) {
        appStore.setShowAlert('error', getExceptionMessage(error))
      }
    }

    if (loading) return <Loader show />

    return (
      <Box sx={{ bgcolor: theme.palette.grey[100], minHeight: '100%' }}>
        <RoleFormHeader isEdit={isEdit} onSubmit={onSubmit} />
        <Container sx={{ paddingY: 2 }}>
          <RoleForm formStore={formStore} role={selectedRole} permissions={roleStore.permissions} />
        </Container>
      </Box>
    )
  }),
)
